/**
 * The `.grindfilter` parser: filter text in, a compiled rule list out.
 *
 * A loot filter is **presentation only**. It decides what the player *sees* — which
 * drops get a label, a beam, a sound, a tier colour — and nothing else. It cannot
 * create an item, cannot remove one, and cannot make one takeable: `CMD_PICKUP` is
 * still re-checked server-side for existence, liveness, range and inventory capacity,
 * exactly as it is for a click. A hidden item is a drop the player is not being shown;
 * it is still lying there and still theirs to take. See the module comment in
 * `evaluate.ts` for the pickup boundary in full.
 *
 * Everything here is pure: no Babylon, no Solid, no DOM, no clock, no randomness. A
 * filter is a string and a verdict is a value, which is why both halves can be tested
 * with plain vitest.
 *
 * ## Why parsing fails loudly
 *
 * A filter is player-authored content, and content this repository does not validate
 * is content whose typos never surface — the exact fault `config.Validate()` exists to
 * prevent on the server side. A rule with a misspelt condition would simply never
 * match, so the player would conclude the *filter system* is broken rather than their
 * one bad line. So: every unrecognised keyword, every malformed argument and every
 * rarity that is not one of the four is an error naming the line, the column and what
 * was expected. Nothing is skipped silently, ever.
 *
 * That includes keywords the spec's own example uses. `Sockets`, `RuneNumber` and the
 * rest describe data GRIND's server does not send; accepting them would produce a rule
 * that can never fire, which is precisely the silent failure above. They are rejected
 * with a message saying *why*, rather than with "unknown keyword".
 *
 * ## The grammar
 *
 * Line-oriented, one keyword per line, `#` to end of line is a comment. A `Show` or
 * `Hide` opens a block; every following condition and directive line belongs to it
 * until the next `Show`/`Hide` or the end of the file. Indentation is cosmetic —
 * **chosen**, not specified: the spec's example indents its bodies, but a filter a
 * player has hand-edited in a text box will not survive a rule that whitespace is
 * load-bearing.
 *
 * ```text
 * # Rule 1: uniques are always worth stopping for.
 * Show
 *     Rarity == UNIQUE
 *     SetTier 1
 *     SetBorderColor 255 0 0
 *     SetFontSize 22
 *     PlayAlertSound 1
 *     PlayLightBeam Red
 *     AutoPickup
 *
 * # Rule 2: high-level rare gear, and keep looking so a later rule can restyle it.
 * Show
 *     Rarity >= RARE
 *     ItemLevel >= 60
 *     Stat "physical_damage_pct" >= 10
 *     Continue
 *
 * # Rule 3: everything low-level and dull, in a deep zone, is clutter.
 * Hide
 *     Rarity <= MAGIC
 *     ItemLevel < 30
 *     AreaLevel >= 40
 * ```
 *
 * Conditions inside one block are ANDed. Two lines about the same field are also
 * ANDed, so `ItemLevel >= 30` with `ItemLevel <= 60` is a band rather than the second
 * line quietly replacing the first. Directives are not ANDed — a repeated directive
 * takes its last value, which is the only useful reading of writing it twice
 * (**chosen**).
 */

import { DOLL_SLOTS, RARITY_ORDER, type DollSlot, type ItemRarity } from '../../net/protocol';

// ── The rule language ───────────────────────────────────────────────────────

/** What a matching rule does with the item. */
export type FilterAction = 'SHOW' | 'HIDE';

/**
 * Numeric comparison operators.
 *
 * `=` is accepted as a synonym for `==` when parsing, because a player writing one
 * `=` means equality and erroring on it teaches nothing (**chosen**). Only `==`
 * survives into the compiled rule, so the evaluator has one spelling to switch on.
 */
export type CompareOp = '==' | '!=' | '<' | '<=' | '>' | '>=';

/**
 * The four priority tiers from the audio-visual table in
 * `doc/grind/design/LOOT-FILTER.md`: 1 is godly, 4 is trash.
 *
 * Lower is louder, deliberately — a tier is a rank, and the spec numbers it that way.
 */
export type FilterTier = 1 | 2 | 3 | 4;

/** Every condition a rule can carry. `line` is kept so a verdict is traceable. */
export type Condition =
  /** `Rarity >= RARE` — an ordered comparison against one rarity. */
  | { kind: 'RARITY_CMP'; op: CompareOp; rarity: ItemRarity; line: number }
  /** `Rarity NORMAL MAGIC` — membership in a set, no ordering implied. */
  | { kind: 'RARITY_IN'; rarities: readonly ItemRarity[]; line: number }
  | { kind: 'ITEM_LEVEL'; op: CompareOp; value: number; line: number }
  | { kind: 'AREA_LEVEL'; op: CompareOp; value: number; line: number }
  | { kind: 'AFFIX_COUNT'; op: CompareOp; value: number; line: number }
  /** `Name "doom" "bane"` — case-insensitive substring, matching if *any* hits. */
  | { kind: 'NAME'; needles: readonly string[]; line: number }
  /** The same, against the catalogue base id rather than the rolled name. */
  | { kind: 'BASE_TYPE'; needles: readonly string[]; line: number }
  /** `Class MAIN_HAND OFF_HAND` — the item's paper-doll slot. */
  | { kind: 'CLASS'; slots: readonly DollSlot[]; line: number }
  /** `Stat "physical_damage_pct" >= 10` — one affix's rolled value. */
  | { kind: 'STAT'; stat: string; op: CompareOp; value: number; line: number };

/** Which field a condition reads. Used to warn about rules that cannot match. */
export type ConditionKind = Condition['kind'];

/**
 * How a matched item is presented. Every field is optional: a rule that only says
 * `Show` is a rule about visibility and nothing else, and the HUD keeps its defaults.
 *
 * Colours are `#rrggbb` strings rather than triples because that is what both
 * consumers want — a CSS custom property and a Babylon `Color3.FromHexString`.
 */
export interface FilterStyle {
  /** `SetTextColor r g b`. */
  textColour?: string;
  /** `SetBorderColor r g b`. */
  borderColour?: string;
  /** `SetFontSize n`, in CSS pixels. */
  fontSize?: number;
  /**
   * `PlayAlertSound n`.
   *
   * An opaque small integer, not a filename: every sound effect in this client is
   * synthesised in `audio/cues.ts`, so a filter naming an asset would be naming
   * something that does not exist. The caller maps the id to a cue.
   */
  soundId?: number;
  /** `PlayLightBeam Red` or `PlayLightBeam 255 0 0`, as `#rrggbb`. */
  beamColour?: string;
}

/** One compiled `Show`/`Hide` block. */
export interface FilterRule {
  /** Position in the rule list, 0-based. First match wins, so this is precedence. */
  index: number;
  /** Source line of the `Show`/`Hide` keyword, for error and debug output. */
  line: number;
  action: FilterAction;
  conditions: readonly Condition[];
  style: FilterStyle;
  /** `SetTier n`, or absent to let the rarity decide. */
  tier?: FilterTier;
  /** `AutoPickup` / `AutoPickup Off`, or absent to leave the tier rule in charge. */
  autoPickup?: boolean;
  /** `Continue` — keep evaluating later rules instead of stopping here. */
  continues: boolean;
}

/** A parsed filter, ready to evaluate. */
export interface CompiledFilter {
  rules: readonly FilterRule[];
}

/**
 * A filter with no rules: every item falls through to the built-in default.
 *
 * This is what the HUD should hold before a player has loaded anything, and it is a
 * frozen constant rather than a factory so an accidental push is a type error.
 */
export const EMPTY_FILTER: CompiledFilter = Object.freeze({ rules: Object.freeze([]) });

// ── Errors ──────────────────────────────────────────────────────────────────

/** One problem with the filter text, located precisely enough to fix. */
export interface FilterDiagnostic {
  /** 1-based, as an editor counts. */
  line: number;
  /** 1-based column of the offending token. */
  column: number;
  /** What was expected, and what was found instead. */
  message: string;
  /** The source line, verbatim, so a caller can render it without the original text. */
  source: string;
}

/**
 * Thrown by `parseFilter` on the first problem.
 *
 * Loading a filter is all-or-nothing: running a filter with one rule quietly missing
 * is worse than not loading it, because the player sees *most* of what they asked for
 * and has no reason to suspect the file. An editor that wants every problem at once
 * calls `diagnoseFilter` instead.
 */
export class FilterParseError extends Error {
  readonly line: number;
  readonly column: number;
  readonly source: string;
  /** The expectation on its own, without the location prefix `message` carries. */
  readonly detail: string;

  constructor(diagnostic: FilterDiagnostic) {
    super(
      `grindfilter line ${diagnostic.line}, column ${diagnostic.column}: ${diagnostic.message}`,
    );
    this.name = 'FilterParseError';
    this.line = diagnostic.line;
    this.column = diagnostic.column;
    this.source = diagnostic.source;
    this.detail = diagnostic.message;
  }

  /** The diagnostic this was built from, for a caller collecting them. */
  get diagnostic(): FilterDiagnostic {
    return { line: this.line, column: this.column, message: this.detail, source: this.source };
  }
}

/**
 * A diagnostic as three lines of text: the location, the source, and a caret.
 *
 * The caret is the whole point — "column 14" is a number, a caret under the wrong
 * word is an answer.
 */
export function formatDiagnostic(d: FilterDiagnostic): string {
  const caret = `${' '.repeat(Math.max(0, d.column - 1))}^`;
  return `line ${d.line}: ${d.message}\n  ${d.source}\n  ${caret}`;
}

// ── Vocabularies ────────────────────────────────────────────────────────────

/**
 * Keywords the spec's example filter uses that this game has no data for.
 *
 * Each says what is missing rather than "unknown keyword", because the player did
 * nothing wrong — they copied the spec. Implementing one of these means deleting its
 * entry here in the same change, the same way a stat key must be added in three places
 * at once.
 */
const UNSUPPORTED: Record<string, string> = {
  SOCKETS: 'GRIND items have no sockets, so this condition could never match',
  LINKEDSOCKETS: 'GRIND items have no sockets, so this condition could never match',
  RUNENUMBER: 'GRIND has no runes, so this condition could never match',
  QUALITY: 'GRIND items have no quality value, so this condition could never match',
  STACKSIZE: 'GRIND items do not stack, so this condition could never match',
  CORRUPTED: 'GRIND has no corruption, so this condition could never match',
  IDENTIFIED: 'every GRIND drop arrives identified, so this condition could never match',
  GEMLEVEL: 'GRIND has no gems, so this condition could never match',
  SETBACKGROUNDCOLOR: 'the label has no background colour of its own; use SetBorderColor',
  MINIMAPICON: 'GRIND has no minimap',
  PLAYEFFECT: 'use PlayLightBeam, which is the beam the renderer actually draws',
  CUSTOMALERTSOUND: 'every sound effect is synthesised, not loaded; use PlayAlertSound',
};

/**
 * Named beam colours.
 *
 * A closed palette rather than free-form CSS colour names — **chosen**. The beam is
 * an emissive mesh in a near-black zone, and half of CSS's 148 names are too dark to
 * see there at all. A player who wants an exact shade writes the triple.
 */
const BEAM_COLOURS: Record<string, string> = {
  RED: '#ff3b30',
  ORANGE: '#ff9500',
  YELLOW: '#ffd60a',
  GREEN: '#34c759',
  CYAN: '#32ade6',
  BLUE: '#4a7fd8',
  PURPLE: '#af52de',
  WHITE: '#f2f2f7',
  GREY: '#8e8e93',
  GRAY: '#8e8e93',
};

/** The four rarities, for an error message that lists them. */
const RARITY_LIST = RARITY_ORDER.join(', ');

/** Highest `PlayAlertSound` id. Sixteen cues is far more than the HUD will define. */
const MAX_SOUND_ID = 15;

/** Bounds on `SetFontSize`. Below 8 a label is unreadable; above 48 it is a banner. */
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 48;

// ── Tokenising ──────────────────────────────────────────────────────────────

interface Token {
  text: string;
  /** 1-based column of the token's first character. */
  column: number;
  /** True when the text came from a quoted string, so it is never a keyword. */
  quoted: boolean;
}

/** Characters that make up an operator, so `>=10` tokenises as `>=` then `10`. */
const OPERATOR_CHARS = new Set(['<', '>', '=', '!']);

/**
 * Splits one line into tokens, dropping a trailing `#` comment.
 *
 * A `#` inside quotes is kept: an item called `Sigil #3` is a name, not a comment.
 */
function tokenise(line: string, lineNumber: number): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < line.length) {
    const ch = line[i]!;

    if (ch === ' ' || ch === '\t' || ch === '\r') {
      i += 1;
      continue;
    }

    if (ch === '#') break;

    if (ch === '"') {
      const start = i;
      i += 1;
      let text = '';
      while (i < line.length && line[i] !== '"') {
        text += line[i];
        i += 1;
      }
      if (i >= line.length) {
        throw new FilterParseError({
          line: lineNumber,
          column: start + 1,
          message: 'unterminated quoted string — add the closing "',
          source: line,
        });
      }
      i += 1; // the closing quote
      tokens.push({ text, column: start + 1, quoted: true });
      continue;
    }

    if (OPERATOR_CHARS.has(ch)) {
      const start = i;
      let text = '';
      while (i < line.length && OPERATOR_CHARS.has(line[i]!)) {
        text += line[i];
        i += 1;
      }
      tokens.push({ text, column: start + 1, quoted: false });
      continue;
    }

    const start = i;
    let text = '';
    while (i < line.length) {
      const c = line[i]!;
      const ends =
        c === ' ' || c === '\t' || c === '\r' || c === '#' || c === '"' || OPERATOR_CHARS.has(c);
      if (ends) break;
      text += c;
      i += 1;
    }
    tokens.push({ text, column: start + 1, quoted: false });
  }

  return tokens;
}

// ── Parsing ─────────────────────────────────────────────────────────────────

/**
 * The parser's per-line cursor.
 *
 * A tiny class rather than index arithmetic at every call site, purely so that every
 * error carries a column without each rule remembering to compute one.
 */
class Line {
  private at = 1;

  constructor(
    readonly number: number,
    readonly source: string,
    private readonly tokens: readonly Token[],
  ) {}

  /** The keyword token, upper-cased. Never called on a blank line. */
  get keyword(): string {
    return (this.tokens[0]?.text ?? '').toUpperCase();
  }

  /**
   * The keyword as the player wrote it.
   *
   * Every message quotes this rather than `keyword`: telling someone their
   * "SETTEXTCOLOR" is wrong when they typed `SetTextColor` makes them hunt for a line
   * that is not in their file.
   */
  get word(): string {
    return this.tokens[0]?.text ?? '';
  }

  get keywordColumn(): number {
    return this.tokens[0]?.column ?? 1;
  }

  /** Arguments after the keyword. */
  get args(): readonly Token[] {
    return this.tokens.slice(1);
  }

  /** Column just past the last token, where a missing argument belongs. */
  get endColumn(): number {
    const last = this.tokens[this.tokens.length - 1];
    return last ? last.column + last.text.length : 1;
  }

  fail(message: string, column?: number): never {
    throw new FilterParseError({
      line: this.number,
      column: column ?? this.at,
      message,
      source: this.source,
    });
  }

  /** Records where the next error should point. */
  point(token: Token | undefined): void {
    this.at = token ? token.column : this.endColumn;
  }
}

/** Whether a token is one of the six comparison operators. */
function asOperator(token: Token | undefined): CompareOp | null {
  if (!token || token.quoted) return null;
  switch (token.text) {
    case '=':
    case '==':
      return '==';
    case '!=':
      return '!=';
    case '<':
      return '<';
    case '<=':
      return '<=';
    case '>':
      return '>';
    case '>=':
      return '>=';
    default:
      return null;
  }
}

/** A number, or an error naming what was expected instead. */
function requireNumber(line: Line, token: Token | undefined, what: string): number {
  line.point(token);
  if (!token) line.fail(`expected ${what}, but the line ends`);
  const value = Number(token.text);
  if (token.text === '' || !Number.isFinite(value)) {
    line.fail(`expected ${what}, got "${token.text}"`, token.column);
  }
  return value;
}

function requireInteger(line: Line, token: Token | undefined, what: string): number {
  const value = requireNumber(line, token, what);
  if (!Number.isInteger(value)) {
    line.fail(`expected ${what}, got the fraction "${token?.text}"`, token?.column);
  }
  return value;
}

/**
 * Parses `[op] number` — the tail of every numeric condition.
 *
 * A bare number means `==` (**chosen**). The spec always writes an operator, and
 * defaulting to `>=` "because that is what people mean" would make `ItemLevel 70`
 * quietly match an item level 90 item.
 */
function numericTail(line: Line, what: string): { op: CompareOp; value: number } {
  const args = line.args;
  const first = args[0];
  const op = asOperator(first);

  if (op === null) {
    if (args.length > 1) {
      line.point(args[1]);
      line.fail(
        `expected one value after ${line.word}, got ${args.length} — ` +
          'write two lines to express a range',
      );
    }
    return { op: '==', value: requireInteger(line, first, what) };
  }

  if (args.length > 2) {
    line.point(args[2]);
    line.fail(`expected a single value after "${line.word} ${first?.text}"`);
  }
  return { op, value: requireInteger(line, args[1], what) };
}

/** Upper-cases an unquoted token and rejects a quoted one. */
function requireBareWord(line: Line, token: Token | undefined, what: string): string {
  line.point(token);
  if (!token) line.fail(`expected ${what}, but the line ends`);
  if (token.quoted) line.fail(`expected ${what} without quotes, got "${token.text}"`, token.column);
  return token.text;
}

function requireRarity(line: Line, token: Token | undefined): ItemRarity {
  const word = requireBareWord(line, token, `a rarity (${RARITY_LIST})`).toUpperCase();
  // Four rarities, not five. LEGENDARY was removed from the design and a filter
  // naming it is a filter written against a game that does not exist — saying so is
  // more use than "unknown rarity".
  if (word === 'LEGENDARY') {
    line.fail(
      `LEGENDARY is not a GRIND rarity — there are four: ${RARITY_LIST}`,
      token?.column,
    );
  }
  if (!(RARITY_ORDER as readonly string[]).includes(word)) {
    line.fail(`expected one of ${RARITY_LIST}, got "${token?.text}"`, token?.column);
  }
  return word as ItemRarity;
}

/** `SetBorderColor r g b` / `SetTextColor r g b` → `#rrggbb`. */
function parseColour(line: Line): string {
  const args = line.args;
  if (args.length !== 3) {
    line.point(args[3] ?? args[args.length - 1]);
    line.fail(`expected three channel values 0-255 after ${line.word}, got ${args.length}`);
  }
  const channels = args.map((token) => {
    const value = requireInteger(line, token, 'a colour channel 0-255');
    if (value < 0 || value > 255) {
      line.fail(`a colour channel is 0-255, got ${value}`, token.column);
    }
    return value;
  });
  return channelsToHex(channels[0]!, channels[1]!, channels[2]!);
}

function channelsToHex(r: number, g: number, b: number): string {
  const hex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

/** `PlayLightBeam Red` or `PlayLightBeam 255 0 0`. */
function parseBeamColour(line: Line): string {
  const args = line.args;
  if (args.length === 1) {
    const name = requireBareWord(line, args[0], 'a beam colour name or three 0-255 channels');
    const hex = BEAM_COLOURS[name.toUpperCase()];
    if (!hex) {
      line.fail(
        `unknown beam colour "${name}" — use one of ` +
          `${Object.keys(BEAM_COLOURS).join(', ')}, or three 0-255 channels`,
        args[0]?.column,
      );
    }
    return hex;
  }
  return parseColour(line);
}

/** One or more strings, for `Name` / `BaseType`. Quoted or bare, both allowed. */
function requireNeedles(line: Line): string[] {
  const args = line.args;
  if (args.length === 0) {
    line.point(undefined);
    line.fail(`expected at least one text value after ${line.word}`);
  }
  // Lower-cased at parse time, so evaluation is a plain `includes` per candidate
  // instead of a `toLowerCase()` per item per rule per frame.
  return args.map((token) => token.text.toLowerCase());
}

/** The condition and directive keywords, so an unknown one can suggest neighbours. */
const KNOWN_KEYWORDS = [
  'Show',
  'Hide',
  'Rarity',
  'ItemLevel',
  'AreaLevel',
  'AffixCount',
  'Name',
  'BaseType',
  'Class',
  'Stat',
  'SetTier',
  'SetTextColor',
  'SetBorderColor',
  'SetFontSize',
  'PlayAlertSound',
  'PlayLightBeam',
  'AutoPickup',
  'Continue',
] as const;

/** A rule under construction. `conditions` and `style` are filled line by line. */
interface Draft {
  index: number;
  line: number;
  action: FilterAction;
  conditions: Condition[];
  style: FilterStyle;
  tier?: FilterTier;
  autoPickup?: boolean;
  continues: boolean;
}

/**
 * Applies one non-blank line to the current block.
 *
 * Returns the new current block when the line opened one. Throws on anything it does
 * not understand — including a condition with no block, which is a filter whose author
 * forgot the `Show` and would otherwise have had their whole rule ignored.
 */
function applyLine(line: Line, current: Draft | null, rules: Draft[]): Draft | null {
  const keyword = line.keyword;

  if (keyword === 'SHOW' || keyword === 'HIDE') {
    if (line.args.length > 0) {
      line.point(line.args[0]);
      line.fail(
        `${keyword === 'SHOW' ? 'Show' : 'Hide'} takes no arguments — ` +
          'put each condition on its own line',
      );
    }
    const draft: Draft = {
      index: rules.length,
      line: line.number,
      action: keyword === 'SHOW' ? 'SHOW' : 'HIDE',
      conditions: [],
      style: {},
      continues: false,
    };
    rules.push(draft);
    return draft;
  }

  const unsupported = UNSUPPORTED[keyword];
  if (unsupported) {
    line.fail(`"${line.word}" is not supported: ${unsupported}`, line.keywordColumn);
  }

  if (!current) {
    line.fail(
      `"${line.word}" belongs inside a rule — add a "Show" or "Hide" line above it`,
      line.keywordColumn,
    );
  }

  switch (keyword) {
    case 'RARITY': {
      const args = line.args;
      const op = asOperator(args[0]);
      if (op !== null) {
        if (args.length > 2) {
          line.point(args[2]);
          line.fail('an operator compares against a single rarity — drop the extra values');
        }
        current.conditions.push({
          kind: 'RARITY_CMP',
          op,
          rarity: requireRarity(line, args[1]),
          line: line.number,
        });
        return current;
      }
      if (args.length === 0) {
        line.point(undefined);
        line.fail(`expected a rarity (${RARITY_LIST}) or an operator after Rarity`);
      }
      current.conditions.push({
        kind: 'RARITY_IN',
        rarities: args.map((token) => requireRarity(line, token)),
        line: line.number,
      });
      return current;
    }

    case 'ITEMLEVEL': {
      const { op, value } = numericTail(line, 'an item level');
      current.conditions.push({ kind: 'ITEM_LEVEL', op, value, line: line.number });
      return current;
    }

    case 'AREALEVEL': {
      const { op, value } = numericTail(line, 'an area level');
      current.conditions.push({ kind: 'AREA_LEVEL', op, value, line: line.number });
      return current;
    }

    case 'AFFIXCOUNT': {
      const { op, value } = numericTail(line, 'an affix count');
      current.conditions.push({ kind: 'AFFIX_COUNT', op, value, line: line.number });
      return current;
    }

    case 'NAME':
      current.conditions.push({ kind: 'NAME', needles: requireNeedles(line), line: line.number });
      return current;

    case 'BASETYPE':
      current.conditions.push({
        kind: 'BASE_TYPE',
        needles: requireNeedles(line),
        line: line.number,
      });
      return current;

    case 'CLASS': {
      const args = line.args;
      if (args.length === 0) {
        line.point(undefined);
        line.fail('expected at least one equipment slot after Class');
      }
      // Validated against DOLL_SLOTS, the same closed list the server keys equipment
      // by. A typo like MAINHAND would otherwise be a rule that never fires.
      const slots = args.map((token) => {
        const word = token.text.toUpperCase();
        if (!(DOLL_SLOTS as readonly string[]).includes(word)) {
          line.fail(
            `"${token.text}" is not an equipment slot — one of ${DOLL_SLOTS.join(', ')}`,
            token.column,
          );
        }
        return word as DollSlot;
      });
      current.conditions.push({ kind: 'CLASS', slots, line: line.number });
      return current;
    }

    case 'STAT': {
      const args = line.args;
      const stat = args[0];
      line.point(stat);
      if (!stat) line.fail('expected a stat key, e.g. Stat "physical_damage_pct" >= 10');
      // The stat vocabulary is the server's and it is closed, but the client is not
      // handed the list — mirroring `config.StatKeys` here would be a second source of
      // truth that silently rots. So the *spelling convention* is enforced instead:
      // lower_snake_case, as every key in affixes.yaml is written. That catches
      // PHYS_DMG_PCT, which is the mistake people actually make.
      if (!/^[a-z][a-z0-9_]*$/.test(stat.text)) {
        line.fail(
          `"${stat.text}" is not a stat key — keys are lower_snake_case, ` +
            'e.g. "physical_damage_pct"',
          stat.column,
        );
      }
      const rest = args.slice(1);
      const op = asOperator(rest[0]);
      if (op === null) {
        line.point(rest[0]);
        line.fail(
          `expected a comparison after Stat "${stat.text}", ` +
            'e.g. Stat "physical_damage_pct" >= 10',
        );
      }
      if (rest.length > 2) {
        line.point(rest[2]);
        line.fail(`expected a single value after Stat "${stat.text}" ${rest[0]?.text}`);
      }
      current.conditions.push({
        kind: 'STAT',
        stat: stat.text,
        op,
        // Fractional, unlike every other numeric: an affix roll is not an integer.
        value: requireNumber(line, rest[1], 'a stat value'),
        line: line.number,
      });
      return current;
    }

    case 'SETTIER': {
      const value = requireInteger(line, line.args[0], 'a tier 1-4');
      if (value < 1 || value > 4) {
        line.fail(`a tier is 1 (godly) to 4 (trash), got ${value}`, line.args[0]?.column);
      }
      current.tier = value as FilterTier;
      return current;
    }

    case 'SETTEXTCOLOR':
      current.style.textColour = parseColour(line);
      return current;

    case 'SETBORDERCOLOR':
      current.style.borderColour = parseColour(line);
      return current;

    case 'SETFONTSIZE': {
      const what = `a font size ${MIN_FONT_SIZE}-${MAX_FONT_SIZE}`;
      const value = requireInteger(line, line.args[0], what);
      if (value < MIN_FONT_SIZE || value > MAX_FONT_SIZE) {
        line.fail(
          `a font size is ${MIN_FONT_SIZE}-${MAX_FONT_SIZE} pixels, got ${value}`,
          line.args[0]?.column,
        );
      }
      current.style.fontSize = value;
      return current;
    }

    case 'PLAYALERTSOUND': {
      const value = requireInteger(line, line.args[0], `a sound id 0-${MAX_SOUND_ID}`);
      if (value < 0 || value > MAX_SOUND_ID) {
        line.fail(`a sound id is 0-${MAX_SOUND_ID}, got ${value}`, line.args[0]?.column);
      }
      current.style.soundId = value;
      return current;
    }

    case 'PLAYLIGHTBEAM':
      current.style.beamColour = parseBeamColour(line);
      return current;

    case 'AUTOPICKUP': {
      const args = line.args;
      if (args.length === 0) {
        current.autoPickup = true;
        return current;
      }
      const word = requireBareWord(line, args[0], 'On or Off').toUpperCase();
      if (args.length > 1 || (word !== 'ON' && word !== 'OFF')) {
        line.fail(`expected "AutoPickup", "AutoPickup On" or "AutoPickup Off"`, args[0]?.column);
      }
      // Off exists so a filter can keep a tier-2 item out of the vacuum without
      // demoting its label — the two are separate decisions (**chosen**).
      current.autoPickup = word === 'ON';
      return current;
    }

    case 'CONTINUE':
      if (line.args.length > 0) {
        line.point(line.args[0]);
        line.fail('Continue takes no arguments');
      }
      current.continues = true;
      return current;

    default:
      line.fail(
        `unknown keyword "${line.word}" — expected one of ${KNOWN_KEYWORDS.join(', ')}`,
        line.keywordColumn,
      );
  }
}

/** Splits text into lines, tolerating CRLF and a final newline. */
function sourceLines(text: string): string[] {
  return text.split('\n').map((l) => (l.endsWith('\r') ? l.slice(0, -1) : l));
}

/**
 * Parses filter text, throwing on the first problem.
 *
 * The rule list is returned in source order, and source order **is** precedence: the
 * evaluator takes the first rule that matches. That mirrors the spec's own
 * `evaluateItemDrop`, and it is the behaviour every filter in the genre has, so a
 * player's instinct about rule ordering is right.
 */
export function parseFilter(text: string): CompiledFilter {
  const lines = sourceLines(text);
  const drafts: Draft[] = [];
  let current: Draft | null = null;

  for (let i = 0; i < lines.length; i += 1) {
    const source = lines[i]!;
    const tokens = tokenise(source, i + 1);
    if (tokens.length === 0) continue;
    current = applyLine(new Line(i + 1, source, tokens), current, drafts);
  }

  return { rules: drafts.map(finish) };
}

function finish(draft: Draft): FilterRule {
  const rule: FilterRule = {
    index: draft.index,
    line: draft.line,
    action: draft.action,
    conditions: draft.conditions,
    style: draft.style,
    continues: draft.continues,
  };
  if (draft.tier !== undefined) rule.tier = draft.tier;
  if (draft.autoPickup !== undefined) rule.autoPickup = draft.autoPickup;
  return rule;
}

/**
 * Every problem in the text at once, for an in-game editor.
 *
 * Recovery is per line: a bad line is reported and dropped, and parsing continues
 * with the next one. Reporting one error at a time would make fixing a two-hundred
 * line filter a two-hundred round trip chore (**chosen** — the spec asks for an
 * in-game editor but says nothing about how it reports).
 *
 * The rules this recovers are deliberately *not* returned. A partially parsed filter
 * is exactly the silent half-failure this module exists to prevent; use `parseFilter`
 * for anything the game will actually run.
 */
export function diagnoseFilter(text: string): FilterDiagnostic[] {
  const lines = sourceLines(text);
  const diagnostics: FilterDiagnostic[] = [];
  const drafts: Draft[] = [];
  let current: Draft | null = null;

  for (let i = 0; i < lines.length; i += 1) {
    const source = lines[i]!;
    try {
      const tokens = tokenise(source, i + 1);
      if (tokens.length === 0) continue;
      current = applyLine(new Line(i + 1, source, tokens), current, drafts);
    } catch (err) {
      if (err instanceof FilterParseError) {
        diagnostics.push(err.diagnostic);
        continue;
      }
      throw err;
    }
  }

  return diagnostics;
}

/** Whether text parses. A convenience for a validate button, nothing more. */
export function isValidFilter(text: string): boolean {
  return diagnoseFilter(text).length === 0;
}
